import { has } from 'fp-ts/lib/ReadonlyRecord';
import type { AnyNonNullish } from 'utils/types/AnyNonNullish';

/**
 * Return list of object keys that retains identity until the set of keys is actually changed
 */
export const makeMemoizedGetObjectKeys: <TKey extends string = string>() => (
  o: AnyNonNullish,
) => TKey[] = <TKey extends string = string>() => {
  let lastKeys: TKey[] = [];
  let lastObject: AnyNonNullish | null = null;
  return (o) => {
    // object did not change -> keys did not change
    if (o === lastObject) {
      return lastKeys;
    }

    lastObject = o;
    const currentKeys = Object.keys(o) as TKey[];

    // keys differ -> update the result
    if (
      currentKeys.length !== lastKeys.length ||
      lastKeys.some((key) => !has(key, o))
    ) {
      lastKeys = currentKeys;
    }

    return lastKeys;
  };
};

/**
 * Get memoized object keys fn with nested object accessor to build memoized selectors
 * as one-liners, e.g. `makeMemoizedGetNestedObjectKeys(prop('graph', 'nodes'))`
 */
export const makeMemoizedGetNestedObjectKeys = <
  T extends AnyNonNullish,
  TKey extends string = string,
>(
  getNestedObject: (obj: T) => AnyNonNullish,
): ((obj: T) => TKey[]) => {
  const getObjectKeys = makeMemoizedGetObjectKeys<TKey>();
  return (obj: T) => getObjectKeys(getNestedObject(obj));
};
